<template>
  <BaseModal :title="'Live streaming'" @close="closeModal">
    <template #modalContent>
      <div class="lsm">
        <header class="lsm-head">
          <div class="lsm-head-row">
            <div>
              <p class="lsm-eyebrow">
                <span class="lsm-eyebrow-dot"></span>
                Live streaming
              </p>
              <h2 class="lsm-title">
                Where would you like to <span class="accent">stream</span>?
              </h2>
              <p class="lsm-sub">
                Push your cast to YouTube, Facebook, Twitch, LinkedIn, or
                Decast's native player.
              </p>
            </div>
            <div class="lsm-summary">
              <div class="lsm-summary-count">{{ activeCount }}</div>
              <div class="lsm-summary-label">
                active<br />destination{{ activeCount === 1 ? '' : 's' }}
              </div>
            </div>
          </div>
        </header>

        <section class="lsm-section">
          <div class="lsm-section-label">External destinations</div>
          <div class="lsm-platforms">
            <button
              v-for="p in platforms"
              :key="p.key"
              type="button"
              class="lsm-platform"
              :class="[p.key, { connected: p.connected }]"
              @click="setActiveModal(p.action)"
            >
              <span class="lsm-platform-accent"></span>
              <span class="lsm-platform-icon" v-html="p.icon"></span>
              <span class="lsm-platform-body">
                <span class="lsm-platform-name">{{ p.label }}</span>
                <span class="lsm-platform-state">
                  <span v-if="p.connected" class="lsm-pulse"></span>
                  {{ p.connected ? 'Connected' : 'Tap to connect' }}
                </span>
              </span>
            </button>
          </div>
        </section>

        <section class="lsm-section">
          <div class="lsm-section-label">Decast streaming</div>
          <label class="lsm-toggle" :class="{ on: VWStream }">
            <input v-model="VWStream" type="checkbox" :disabled="isLoading" />
            <span class="lsm-toggle-track">
              <span class="lsm-toggle-thumb"></span>
            </span>
            <span class="lsm-toggle-text">
              <span class="lsm-toggle-title">
                Stream to Decast player
                <span v-if="VWStream" class="lsm-toggle-badge">On</span>
              </span>
              <span class="lsm-toggle-desc">
                You'll get a public stream page URL by email when the cast goes
                live.
              </span>
            </span>
          </label>
        </section>

        <footer class="lsm-actions">
          <button type="button" class="lsm-btn ghost" @click="closeModal">
            Cancel
          </button>
          <button
            type="button"
            class="lsm-btn primary"
            :disabled="isLoading"
            @click="updateCastWithStream"
          >
            {{ isLoading ? 'Loading…' : 'Save changes' }}
          </button>
        </footer>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/common/BaseModal.vue';

// Inline brand SVGs (small, currentColor for connected variant).
const ICONS = {
  youtube:
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136C4.495 20.455 12 20.455 12 20.455s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.546 15.568V8.432L15.818 12l-6.272 3.568z"/></svg>',
  facebook:
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.017 1.792-4.683 4.533-4.683 1.312 0 2.686.235 2.686.235v2.97h-1.514c-1.491 0-1.956.93-1.956 1.886v2.262h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>',
  twitch:
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>',
  linkedin:
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
};

export default {
  name: 'LiveStreamModal',
  props: [
    'closeModal',
    'setActiveModal',
    'castDetails',
    'stepThreeProps',
    'handleEditCast',
  ],
  components: { BaseModal },
  data() {
    return {
      isLoading: false,
      VWStream: false,
      youtube: '',
      youtubeSecret: '',
      facebook: '',
      facebookSecret: '',
      twitch: '',
      twitchSecret: '',
      linkedin: '',
      linkedinSecret: '',
    };
  },
  computed: {
    platforms() {
      return [
        {
          key: 'youtube',
          label: 'YouTube',
          icon: ICONS.youtube,
          connected: !!this.youtubeSecret,
          action: 'youtubeStreamModal',
        },
        {
          key: 'facebook',
          label: 'Facebook',
          icon: ICONS.facebook,
          connected: !!this.facebookSecret,
          action: 'facebookStreamModal',
        },
        {
          key: 'twitch',
          label: 'Twitch',
          icon: ICONS.twitch,
          connected: !!this.twitchSecret,
          action: 'twitchStreamModal',
        },
        {
          key: 'linkedin',
          label: 'LinkedIn',
          icon: ICONS.linkedin,
          connected: !!this.linkedinSecret,
          action: 'linkedinStreamModal',
        },
      ];
    },
    activeCount() {
      return (
        (this.youtubeSecret ? 1 : 0) +
        (this.facebookSecret ? 1 : 0) +
        (this.twitchSecret ? 1 : 0) +
        (this.linkedinSecret ? 1 : 0) +
        (this.VWStream ? 1 : 0)
      );
    },
  },
  mounted() {
    this.isLoading = true;
    this.$store
      .dispatch('cast/editEvent', this.castDetails.public_meeting_id)
      .then((res) => {
        const info = res.data.details;
        // Source of truth for "decast streaming enabled" is the
        // backend-derived `vw_stream` flag (Meeting.has_vw_stream on
        // the server), not a substring match on the RTMP host.
        if (info.vw_stream) {
          this.stepThreeProps.vw_stream = true;
          this.VWStream = true;
        }
        if (info.bbb_stream_url !== null) {
          // eslint-disable-next-line no-eval
          window.eval(info.bbb_stream_url).forEach((item) => {
            const x = item.split('/');
            if (item.match(/youtube/)) {
              this.stepThreeProps.youtube_secret_key = x.pop();
              this.stepThreeProps.youtube_rtmp_url = x.join('/');
              this.stepThreeProps.youtube_stream_url = item;
              this.stepThreeProps.record_youtube = true;
              this.youtubeSecret = this.stepThreeProps.youtube_secret_key;
              this.youtube = this.stepThreeProps.youtube_rtmp_url;
            }
            if (item.match(/facebook/)) {
              this.stepThreeProps.facebook_secret_key = x.pop();
              this.stepThreeProps.facebook_rtmp_url = x.join('/');
              this.stepThreeProps.facebook_stream_url = item;
              this.stepThreeProps.record_facebook = true;
              this.facebookSecret = this.stepThreeProps.facebook_secret_key;
              this.facebook = this.stepThreeProps.facebook_rtmp_url;
            }
            if (item.match(/twitch/)) {
              this.stepThreeProps.twitch_secret_key = x.pop();
              this.stepThreeProps.twitch_rtmp_url = x.join('/');
              this.stepThreeProps.twitch_stream_url = item;
              this.stepThreeProps.record_twich = true;
              this.twitch = this.stepThreeProps.twitch_rtmp_url;
              this.twitchSecret = this.stepThreeProps.twitch_secret_key;
            }
            // LinkedIn ingest lives on bitcod.in (Bitmovin), so match both
            if (item.match(/linkedin|bitcod/)) {
              this.stepThreeProps.linkedin_secret_key = x.pop();
              this.stepThreeProps.linkedin_rtmp_url = x.join('/');
              this.stepThreeProps.linkedin_stream_url = item;
              this.stepThreeProps.record_linkedin = true;
              this.linkedin = this.stepThreeProps.linkedin_rtmp_url;
              this.linkedinSecret = this.stepThreeProps.linkedin_secret_key;
            }
          });
        }
        this.isLoading = false;
      })
      .catch((e) => {
        this.isLoading = false;
        console.log('Error editing', e);
      });
  },
  methods: {
    updateCastWithStream() {
      const streamUrls = [{ vw_stream: 'True' }, { urls: [] }];

      streamUrls[0].vw_stream = this.VWStream ? 'True' : 'False';

      if (this.youtube && this.youtubeSecret) {
        streamUrls[1].urls.push(`${this.youtube}/${this.youtubeSecret}`);
      }
      if (this.facebook && this.facebookSecret) {
        streamUrls[1].urls.push(`${this.facebook}/${this.facebookSecret}`);
      }
      if (this.twitch && this.twitchSecret) {
        streamUrls[1].urls.push(`${this.twitch}/${this.twitchSecret}`);
      }
      if (this.linkedin && this.linkedinSecret) {
        streamUrls[1].urls.push(`${this.linkedin}/${this.linkedinSecret}`);
      }

      const isStreaming = this.VWStream || streamUrls[1].urls.length > 0;

      this.stepThreeProps.is_streaming = isStreaming;
      this.stepThreeProps.vw_stream = streamUrls[0].vw_stream;
      this.stepThreeProps.vw_stream_url = JSON.stringify(streamUrls);
      this.handleEditCast({ updateStream: true });
    },
  },
};
</script>

<style scoped>
.lsm {
  width: 620px;
  max-width: calc(100vw - 32px);
  padding: 10px 6px 4px;
  display: flex;
  flex-direction: column;
  gap: 26px;
  color: #fff;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}

/* ---- HEAD ---- */
.lsm-head {
  padding-bottom: 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.lsm-head-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}
.lsm-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
}
.lsm-eyebrow-dot {
  width: 6px;
  height: 6px;
  background: #d7df23;
  display: inline-block;
}
.lsm-title {
  font-size: 26px;
  font-weight: 700;
  margin: 8px 0 0;
  letter-spacing: -0.01em;
  color: #fff;
  line-height: 1.15;
}
.lsm-title .accent {
  color: #d7df23;
}
.lsm-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  margin: 8px 0 0;
  line-height: 1.55;
  max-width: 38ch;
}

.lsm-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(215, 223, 35, 0.08);
  border-left: 3px solid #d7df23;
  flex-shrink: 0;
}
.lsm-summary-count {
  font-size: 32px;
  font-weight: 700;
  color: #d7df23;
  line-height: 1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.lsm-summary-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.2;
}

/* ---- SECTIONS ---- */
.lsm-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.lsm-section-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  gap: 10px;
}
.lsm-section-label::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
}

/* ---- PLATFORMS ---- */
.lsm-platforms {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.lsm-platform {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  padding: 16px 14px 16px 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease,
    transform 0.15s ease;
  overflow: hidden;
}
.lsm-platform-accent {
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: transparent;
  transition: background 0.15s ease;
}
.lsm-platform.youtube .lsm-platform-accent {
  background: #ff0033;
}
.lsm-platform.facebook .lsm-platform-accent {
  background: #1877f2;
}
.lsm-platform.twitch .lsm-platform-accent {
  background: #9146ff;
}
.lsm-platform.linkedin .lsm-platform-accent {
  background: #0a66c2;
}
.lsm-platform:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.18);
  transform: translateY(-1px);
}
.lsm-platform.connected {
  background: rgba(108, 220, 99, 0.06);
  border-color: rgba(108, 220, 99, 0.35);
}
.lsm-platform.connected:hover {
  background: rgba(108, 220, 99, 0.09);
  border-color: rgba(108, 220, 99, 0.55);
}
.lsm-platform-icon {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.lsm-platform.youtube .lsm-platform-icon {
  color: #ff0033;
}
.lsm-platform.facebook .lsm-platform-icon {
  color: #1877f2;
}
.lsm-platform.twitch .lsm-platform-icon {
  color: #9146ff;
}
.lsm-platform.linkedin .lsm-platform-icon {
  color: #0a66c2;
}
.lsm-platform-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}
.lsm-platform-name {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.005em;
}
.lsm-platform-state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
}
.lsm-platform.connected .lsm-platform-state {
  color: #6cdc63;
}
.lsm-pulse {
  width: 7px;
  height: 7px;
  background: #6cdc63;
  border-radius: 50%;
  position: relative;
  animation: lsm-pulse 1.6s ease-out infinite;
}
@keyframes lsm-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(108, 220, 99, 0.55);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(108, 220, 99, 0);
  }
}

/* ---- DECAST TOGGLE ---- */
.lsm-toggle {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  overflow: hidden;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.lsm-toggle::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: rgba(255, 255, 255, 0.12);
  transition: background 0.15s ease;
}
.lsm-toggle.on {
  background: rgba(215, 223, 35, 0.06);
  border-color: rgba(215, 223, 35, 0.45);
}
.lsm-toggle.on::before {
  background: #d7df23;
}
.lsm-toggle input {
  display: none;
}
.lsm-toggle-track {
  position: relative;
  width: 40px;
  height: 22px;
  background: rgba(255, 255, 255, 0.14);
  flex-shrink: 0;
  margin-top: 1px;
  transition: background 0.15s ease;
}
.lsm-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  background: #fff;
  transition: transform 0.15s ease;
}
.lsm-toggle input:checked + .lsm-toggle-track {
  background: #d7df23;
}
.lsm-toggle input:checked + .lsm-toggle-track .lsm-toggle-thumb {
  transform: translateX(18px);
  background: #0b0c14;
}
.lsm-toggle-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
}
.lsm-toggle-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.005em;
}
.lsm-toggle-badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0b0c14;
  background: #d7df23;
  padding: 2px 8px;
}
.lsm-toggle-desc {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.55;
}

/* ---- ACTIONS ---- */
.lsm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin-top: 4px;
}
.lsm-btn {
  padding: 11px 22px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  font-family: inherit;
  letter-spacing: 0.02em;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}
.lsm-btn.ghost {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.8);
}
.lsm-btn.ghost:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.3);
}
.lsm-btn.primary {
  background: #d7df23;
  color: #0b0c14;
}
.lsm-btn.primary:hover:not(:disabled) {
  background: #e7ef33;
}
.lsm-btn.primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .lsm {
    width: 100%;
  }
  .lsm-head-row {
    flex-direction: column;
  }
  .lsm-summary {
    align-self: flex-start;
  }
  .lsm-platforms {
    grid-template-columns: 1fr;
  }
  .lsm-actions {
    flex-direction: column-reverse;
  }
  .lsm-btn {
    width: 100%;
  }
}
</style>
